import { useState } from "react";
import { useFeature } from "@optimizely/react-sdk";
import moment from "moment";

import { getClimateAlertNames, getWarningNames } from "../../utils/alertUtils";
import {
  mapSubscriptionStatus,
  renderSideLine,
  renderTags,
} from "../../utils/utils";
import DropDownMenu from "../common/DropDownMenu";
import {
  EBackendSubscriptionStatus,
  ESubscriptionStatus,
  IHomeSubscription,
  IProViewHome,
} from "../../interfaces/interfaces";
import { ReactComponent as Key } from "../../img/key.svg";
import { ReactComponent as More } from "../../img/header/More.svg";
import { renderUnitDetailsElementAmount } from "../unitDetails/common/CommonUnitDetailsComponents";

export interface HomeDetailsMainInfoForLargeScreenProps {
  enableBrilliantMaxSubscription?: boolean;
  home: IProViewHome;
  numBrilliantDevices: number;
  numIntegrations: number;
  openCancelBrilliantMaxModal: () => void;
  openEnrollBrilliantMaxModal: () => void;
  openRemoveHomeModal: () => void;
  reactivateBrilliantMax: () => void;
  subscription?: IHomeSubscription | null;
}

const renderSubscriptionButton = (
  status: ESubscriptionStatus,
  isCanceling: boolean,
  openCancelBrilliantMaxModal: () => void,
  openEnrollBrilliantMaxModal: () => void,
  reactivateBrilliantMax: () => void
): JSX.Element => {
  if (status === ESubscriptionStatus.NotSubscribed) {
    return (
      <button
        className="tertiary-btn-small"
        type="button"
        onClick={openEnrollBrilliantMaxModal}
      >
        Enroll in Brilliant Max
      </button>
    );
  }
  if (status === ESubscriptionStatus.SelfManaged) {
    return (
      <button
        className="tertiary-btn-small"
        disabled
        type="button"
        onClick={() => {}}
      >
        Enrolled in Brilliant Max
      </button>
    );
  }
  // Pro-managed subscription that is scheduled to cancel: offer reactivation
  // instead of cancellation. Clicking opens the Stripe portal in a new tab.
  if (isCanceling) {
    return (
      <button
        className="tertiary-btn-small"
        type="button"
        onClick={reactivateBrilliantMax}
      >
        Reactivate Brilliant Max
      </button>
    );
  }
  return (
    <button
      className="tertiary-btn-small tertiary-btn-small-alert"
      type="button"
      onClick={openCancelBrilliantMaxModal}
    >
      Cancel Brilliant Max
    </button>
  );
};

const HomeDetailsMainInfoForLargeScreen: React.FC<
  HomeDetailsMainInfoForLargeScreenProps
> = ({
  enableBrilliantMaxSubscription = false,
  home,
  numBrilliantDevices,
  numIntegrations,
  openCancelBrilliantMaxModal,
  openEnrollBrilliantMaxModal,
  openRemoveHomeModal,
  reactivateBrilliantMax,
  subscription,
}) => {
  const [dropDownMenuVisible, setDropDownMenuVisible] = useState(false);
  const [isFeatureEnabled] = useFeature("brilliant_max_subscription");
  const isBrilliantMaxEnabled =
    isFeatureEnabled || enableBrilliantMaxSubscription;
  const alertDetected = home.alerts.length !== 0;
  const alerts = getClimateAlertNames(home.alerts);
  const warningNames = getWarningNames(home.warnings);
  const hasWarnings = warningNames.length > 0;
  const subscriptionStatus = mapSubscriptionStatus(subscription);
  // A Pro-managed subscription that is scheduled to cancel ends on its current
  // period end instead of renewing, and offers reactivation.
  const isCanceling =
    subscription?.status === EBackendSubscriptionStatus.Canceling;
  return (
    <div className="unit-main-container unit-main-container-pro-view">
      {renderSideLine({
        hasAlerts: alertDetected,
        hasWarnings,
        lineClassName: "unit-main-container-line",
      })}

      <div className="flex-display">
        <div className="unit-names">
          <Key title="Key" className="unit-names-img" />
          <div className="unit-names-info unit-names-info-pro-view">
            <div className="unit-names-info-name unit-title">
              {home.homeName}
            </div>
            {isBrilliantMaxEnabled &&
              subscriptionStatus === ESubscriptionStatus.ProManaged &&
              subscription?.currentPeriodEnd && (
                <div className="unit-description unit-names-info-renewal">
                  {`${
                    isCanceling ? "Subscription ends on" : "Subscription renews"
                  } ${moment(subscription.currentPeriodEnd).format("LL")}`}
                </div>
              )}
            {renderTags({
              alerts,
              subscriptionStatus: isBrilliantMaxEnabled
                ? subscriptionStatus
                : undefined,
              warnings: warningNames,
            })}
          </div>
        </div>
        <div className="unit-details-container-right unit-details-container-right-pro-view">
          <div className="unit-details-container unit-description-small unit-description-small-info">
            <div className="unit-details">
              {renderUnitDetailsElementAmount({
                nameOfSingleDevice: "Brilliant",
                numDevices: numBrilliantDevices,
              })}
            </div>
            <div className="unit-details">
              {renderUnitDetailsElementAmount({
                nameOfSingleDevice: "Integration",
                numDevices: numIntegrations,
              })}
            </div>
          </div>
          {isBrilliantMaxEnabled &&
            subscriptionStatus &&
            renderSubscriptionButton(
              subscriptionStatus,
              isCanceling,
              openCancelBrilliantMaxModal,
              openEnrollBrilliantMaxModal,
              reactivateBrilliantMax
            )}
          <div className="dashboard-settings-more">
            {dropDownMenuVisible && (
              <DropDownMenu>
                <button
                  className="dashboard-settings-more-btn unit-description-small"
                  type="button"
                  onClick={() => {
                    openRemoveHomeModal();
                    setDropDownMenuVisible(false);
                  }}
                >
                  Remove From ProView
                </button>
              </DropDownMenu>
            )}
            <More
              className="dashboard-settings-more-svg"
              onClick={() => setDropDownMenuVisible(!dropDownMenuVisible)}
              title="More Button"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeDetailsMainInfoForLargeScreen;
